import type { EpisodeDiagnosticRowResp, ScalarIndicatorResp } from "@/api";

/**
 * 에피소드 큐레이션 지표의 순수 코어다. 스칼라를 투영하고 데이터셋 안의 위치를 계산한다.
 *
 * 두 축(`action_variance` / `state_neighbor_count`)은 backend `service/diagnostics/curation.py`
 * 가 계산한다. 그 모듈은 **좋고 나쁨을 판정하지 않는다**. 두 축은 선호 방향이 반대다.
 * 근거 논문도 coverage 증가가 곧 좋은 것은 아니라고 명시한다. 그래서 이 모듈도 임계값과
 * 심각도를 만들지 않고, 값과 데이터셋 안의 위치만 계산한다. 밴드 축은 이와 달리 FE 임계값을
 * 가진다. 위치를 색으로 강조하는 규칙은 `overviewEmphasis.ts` 가 소유한다. 그 규칙은 판정이
 * 아니라 분포 양끝을 강조한다.
 *
 * 값은 **컬렉션 안에서만 뜻이 있다**(backend `read.py::_deviation_scalar`). 교차 데이터셋 비교의
 * 전제가 깨졌는지는 서버 로그에만 남고 응답에는 없다. 그래서 소비부는 데이터셋 안의 위치만
 * 보여준다.
 *
 * Owns:
 * - `scalars[]` → 축별 값 투영, 데이터셋 안의 백분위.
 *
 * Does not own:
 * - 적재(모든 페이지 조회) · 렌더 · 문구.
 * - 채널 기록 통계. 타이밍 보기(`timingRows`)가 진단 상세 `channels[]`를 읽는다.
 */

// 서버 스칼라 키. FE 는 밴드 축을 밴드 본문에서 직접 다시 계산하므로 여기서 쓰지 않는다.
// 그러나 밴드 축도 같은 `scalars[]` 에 포함되어 오므로 투영 대상에는 둔다.
const OUTSIDE_REFERENCE_FRACTION_KEY = "outside_reference_fraction";
const ACTION_VARIANCE_KEY = "action_variance";
const STATE_NEIGHBOR_COUNT_KEY = "state_neighbor_count";

/** 백분위를 계산할 수 있는 축. 이 값들은 컬렉션 안에서만 뜻이 있다. */
export type EpisodeDiagnosticAxis =
  | "outsideReferenceFraction"
  | "actionVariance"
  | "stateNeighborCount";

/** 한 에피소드의 진단 값들. 측정할 수 없었던 축은 null 이다. */
export type EpisodeDiagnosticValues = {
  outsideReferenceFraction: number | null;
  actionVariance: number | null;
  stateNeighborCount: number | null;
};

// 서버는 값이 없는 축의 항목을 빼고 보낸다. 키가 없거나 value 가 null 이면 둘 다 "없음" 이다.
function scalarValue(scalars: readonly ScalarIndicatorResp[], key: string): number | null {
  const found = scalars.find((scalar) => scalar.key === key);
  return found?.value ?? null;
}

export function toDiagnosticValues(row: EpisodeDiagnosticRowResp): EpisodeDiagnosticValues {
  return {
    outsideReferenceFraction: scalarValue(row.scalars, OUTSIDE_REFERENCE_FRACTION_KEY),
    actionVariance: scalarValue(row.scalars, ACTION_VARIANCE_KEY),
    stateNeighborCount: scalarValue(row.scalars, STATE_NEIGHBOR_COUNT_KEY),
  };
}

/** 데이터셋 전체의 진단 행. 에피소드 uuid → 축별 값. */
export type EpisodeDiagnosticIndex = ReadonlyMap<string, EpisodeDiagnosticValues>;

export function buildDiagnosticIndex(
  rows: readonly EpisodeDiagnosticRowResp[],
): EpisodeDiagnosticIndex {
  return new Map(rows.map((row) => [row.episode_uuid, toDiagnosticValues(row)] as const));
}

/**
 * 이 값이 표본 안에서 차지하는 백분위(0~1). 값은 자기 이하인 표본의 비율이다. 그래서 최소값도
 * 0 이 아니다(표본 하나면 1). 동점은 모두 같은 값을 받는다. 동점을 순서로 구분할 근거가 없다.
 *
 * 표본이 비면 null 을 돌려준다. "0 번째" 와 "측정할 수 없음" 을 섞지 않는다.
 */
export function percentileRank(sample: readonly number[], value: number): number | null {
  if (sample.length === 0) {
    return null;
  }
  const atOrBelow = sample.filter((entry) => entry <= value).length;
  return atOrBelow / sample.length;
}

/** 축 하나의 표본. 값이 있는 에피소드만 모은다. 값이 없는 에피소드는 분포에서 빠진다. */
export function axisSample(index: EpisodeDiagnosticIndex, axis: EpisodeDiagnosticAxis): number[] {
  const sample: number[] = [];
  for (const values of index.values()) {
    const value = values[axis];
    if (value !== null) {
      sample.push(value);
    }
  }
  return sample;
}
